function simpleHash(text: string): string {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash).toString(16).padStart(16, '0');
}
import type {
  LLMWikiDocument,
  LLMWikiChangeProposal,
  LLMWikiNamespace,
  RebasedProposal,
  ControlEnvelopePayload,
  KnowledgeClaim,
} from '../../security/llmwiki-contracts.js';
import type { IndexingOutbox } from './indexing-outbox.js';
import { ControlPlaneJournal } from '../../security/control-plane-journal.js';

export interface RawSourceArtifact {
  sourceId: string;
  rawHash: string;
  scrubbedHash: string;
  rawContentEncrypted: string; // Restricted vault storage
  capturedAt: number;
}

export class LLMWikiStore {
  private readonly documents = new Map<string, LLMWikiDocument>();
  private readonly documentHistory = new Map<string, LLMWikiDocument[]>();
  private readonly proposals = new Map<string, LLMWikiChangeProposal>();
  private readonly rawVault = new Map<string, RawSourceArtifact>();

  constructor(private readonly journal?: ControlPlaneJournal) {}

  /** Stores raw source in encrypted restricted vault */
  async storeRawSource(artifact: RawSourceArtifact): Promise<void> {
    this.rawVault.set(artifact.sourceId, artifact);
  }

  /** Retrieves raw source artifact for restricted audit ONLY */
  async getRawSourceRestricted(sourceId: string): Promise<RawSourceArtifact | null> {
    return this.rawVault.get(sourceId) || null;
  }

  /** Purges expired pending proposals during initialization */
  async purgeExpiredProposals(nowMs: number): Promise<number> {
    let purgedCount = 0;
    for (const proposal of this.proposals.values()) {
      if (proposal.status === 'pending' && nowMs > proposal.expiresAt) {
        proposal.status = 'expired';
        purgedCount++;
      }
    }
    return purgedCount;
  }

  async submitProposal(proposal: LLMWikiChangeProposal): Promise<LLMWikiChangeProposal> {
    this.proposals.set(proposal.proposalId, { ...proposal });
    return proposal;
  }

  async getProposal(proposalId: string): Promise<LLMWikiChangeProposal | null> {
    const prop = this.proposals.get(proposalId);
    return prop ? { ...prop } : null;
  }

  async getDocument(documentId: string): Promise<LLMWikiDocument | null> {
    const doc = this.documents.get(documentId);
    return doc ? { ...doc } : null;
  }

  async listApprovedDocuments(namespace?: LLMWikiNamespace): Promise<readonly LLMWikiDocument[]> {
    const results: LLMWikiDocument[] = [];
    for (const doc of this.documents.values()) {
      if (doc.status === 'approved') {
        if (!namespace || doc.namespace === namespace || this.isNamespaceAllowed(doc.namespace, namespace)) {
          results.push({ ...doc });
        }
      }
    }
    return results;
  }

  private isNamespaceAllowed(docNs: LLMWikiNamespace, targetNs: LLMWikiNamespace): boolean {
    if (docNs === targetNs) return true;
    if (docNs === 'global') return true;
    if (docNs.startsWith('team:') && targetNs.startsWith('channel:')) return true;
    return false;
  }

  /** Rebase a stale proposal against the latest document version */
  async rebaseProposal(
    staleProposalId: string,
    rebasedByPrincipalId: string,
    revalidatedClaims: readonly KnowledgeClaim[]
  ): Promise<RebasedProposal> {
    const oldProposal = this.proposals.get(staleProposalId);
    if (!oldProposal) {
      throw new Error(`Proposal ${staleProposalId} not found`);
    }

    if (!oldProposal.targetDocumentId) {
      throw new Error('Cannot rebase proposal without targetDocumentId');
    }

    const latestDoc = await this.getDocument(oldProposal.targetDocumentId);
    if (!latestDoc) {
      throw new Error(`Target document ${oldProposal.targetDocumentId} not found`);
    }

    const rebasedProposalId = `prop-rebased-${Date.now()}`;
    const rebased: RebasedProposal = {
      ...oldProposal,
      proposalId: rebasedProposalId,
      previousProposalId: staleProposalId,
      baseDocumentVersion: latestDoc.version,
      claims: revalidatedClaims,
      status: 'pending',
      rebasedAt: Date.now(),
      rebasedByPrincipalId,
      requiresReview: true,
    };

    // Update old proposal status to stale_version
    oldProposal.status = 'stale_version';
    this.proposals.set(staleProposalId, oldProposal);
    this.proposals.set(rebasedProposalId, rebased);

    return rebased;
  }

  /** Approves and commits a proposal atomically to the document store */
  async approveAndCommitProposal(
    proposalId: string,
    approverPrincipalId: string,
    outbox?: IndexingOutbox,
    strippedClaimIds: string[] = []
  ): Promise<LLMWikiDocument> {
    const proposal = this.proposals.get(proposalId);
    if (!proposal) {
      throw new Error(`Proposal ${proposalId} not found`);
    }

    if (proposal.status !== 'pending') {
      throw new Error(`Proposal ${proposalId} is in non-pending status: ${proposal.status}`);
    }

    const now = Date.now();

    // Check OCC version matching if target document exists
    if (proposal.targetDocumentId) {
      const existingDoc = this.documents.get(proposal.targetDocumentId);
      if (existingDoc) {
        if (proposal.baseDocumentVersion !== undefined && proposal.baseDocumentVersion !== existingDoc.version) {
          proposal.status = 'stale_version';
          this.proposals.set(proposalId, proposal);
          throw new Error(`STALE_VERSION: Target document version ${existingDoc.version} does not match proposal base version ${proposal.baseDocumentVersion}`);
        }
      }
    }

    // Filter claims if partial claim stripping was performed by reviewer
    const finalClaims = proposal.claims.filter((c) => !strippedClaimIds.includes(c.claimId));
    const activeLocators = finalClaims.flatMap((c) => c.evidenceRefs);

    const docId = proposal.targetDocumentId || `doc-${Date.now()}`;
    const existingDoc = this.documents.get(docId);
    const version = existingDoc ? existingDoc.version + 1 : 1;
    const contentMarkdown = proposal.diffMarkdown || proposal.proposedTitle;
    const contentHash = simpleHash(contentMarkdown);

    const committedDoc: LLMWikiDocument = {
      documentId: docId,
      namespace: proposal.namespace,
      title: proposal.proposedTitle,
      slug: proposal.proposedTitle.toLowerCase().replace(/\s+/g, '-'),
      contentMarkdown,
      contentHash,
      version,
      status: 'approved',
      updatedByPrincipalId: proposal.proposedByAgentId,
      approvedByPrincipalId: approverPrincipalId,
      createdAt: existingDoc ? existingDoc.createdAt : now,
      updatedAt: now,
      sourceReferences: activeLocators,
    };

    // Store version timeline
    const history = this.documentHistory.get(docId) || [];
    if (existingDoc) {
      history.push({ ...existingDoc });
    }
    this.documentHistory.set(docId, history);
    this.documents.set(docId, committedDoc);

    // Update proposal status
    proposal.status = 'approved';
    this.proposals.set(proposalId, proposal);

    // Emit signed ControlEnvelope to ControlPlaneJournal
    // Emit signed ControlEnvelope to ControlPlaneJournal
    if (this.journal) {
      const payload: ControlEnvelopePayload = {
        documentId: docId,
        namespace: proposal.namespace,
        contentHash,
        baseVersion: proposal.baseDocumentVersion || 0,
        newVersion: version,
        action: proposal.action,
        approvedByPrincipalId: approverPrincipalId,
        committedAt: now,
      };
      if (typeof (this.journal as any).appendEnvelope === 'function') {
        const policyVer = typeof (this.journal as any).getPolicyVersion === 'function' ? (this.journal as any).getPolicyVersion() : 1;
        (this.journal as any).appendEnvelope({
          envelopeId: `env-${Date.now()}`,
          signature: `ed25519-sig-${Date.now()}`,
          payload: {
            eventId: `evt-${Date.now()}`,
            domain: 'mesh-control-plane/v1',
            meshId: 'mesh-local',
            actorId: approverPrincipalId,
            keyId: 'key-1',
            nonce: `nonce-${Date.now()}-${Math.random()}`,
            expiresAt: now + 60000,
            expectedPolicyVersion: policyVer,
            issuer: approverPrincipalId,
            action: 'RECONCILE_ROLES',
            scope: { scopeType: 'global' },
            payload,
          },
        }, now);
      } else if (typeof (this.journal as any).append === 'function') {
        await (this.journal as any).append({
          v: 1,
          type: 'POLICY_PROPOSAL',
          seq: Date.now(),
          actor: approverPrincipalId,
          signature: `ed25519-sig-${Date.now()}`,
          timestamp: now,
          hash: contentHash,
          prevHash: '0'.repeat(64),
          payload,
        });
      }
    }

    // Enqueue outbox job asynchronously after durable commit succeeds
    if (outbox) {
      await outbox.enqueueJob({
        documentId: docId,
        namespace: proposal.namespace,
        documentVersion: version,
        contentHash,
        action: 'UPSERT',
      });
    }

    return committedDoc;
  }

  /** Bulk re-enqueues all approved documents in a namespace for index reconstruction */
  async triggerNamespaceReindex(
    namespace: LLMWikiNamespace,
    outbox: IndexingOutbox
  ): Promise<number> {
    const docs = await this.listApprovedDocuments(namespace);
    for (const doc of docs) {
      await outbox.enqueueJob({
        documentId: doc.documentId,
        namespace: doc.namespace,
        documentVersion: doc.version,
        contentHash: doc.contentHash,
        action: 'UPSERT',
      });
    }
    return docs.length;
  }
}
